import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  RotateCcw, 
  HelpCircle, 
  Mail, 
  ShieldCheck, 
  Download, 
  CreditCard, 
  AlertCircle, 
  ChevronRight,
  ExternalLink,
  Search,
  CheckCircle2,
  Info,
  ArrowLeft
} from 'lucide-react';

const RefundPolicy: React.FC = () => {
  return (
    <div className="min-h-screen pt-24 pb-20 bg-white relative overflow-hidden font-sans">
      {/* Background Accents */}
      <div className="absolute top-0 right-0 w-full h-[600px] bg-[radial-gradient(circle_at:70%_0%,rgba(255,192,203,0.05)_0%,transparent_70%)] -z-10" />
      <div className="absolute bottom-0 left-0 w-full h-[400px] bg-[radial-gradient(circle_at:20%_100%,rgba(6,182,212,0.03)_0%,transparent_60%)] -z-10" />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-start gap-4 mb-12"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-bold text-zinc-400 hover:text-cyan-600 transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Home
          </Link>
          
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-black font-display text-cyan-950 tracking-tighter uppercase">
              Refund <span className="italic text-cyan-600">Policy</span>
            </h1>
            <p className="text-zinc-500 font-medium text-sm leading-relaxed max-w-xl">
              Transparent and fair. Understand our commitment to your satisfaction and the conditions for returns.
            </p>
          </div>

          <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-[0.3em] text-cyan-400 pt-2 border-t border-cyan-600/10 w-full">
            <span>Last Updated: April 28, 2026</span>
            <span className="w-1 h-1 rounded-full bg-cyan-200" />
            <span>Version 1.4</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-8">
          {/* Section 1: Overview and Consumer Rights */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20 group-hover:-translate-y-1 transition-transform">
                <ShieldCheck className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight flex items-center gap-3">
                  1. Overview and Consumer Rights
                  <ChevronRight className="w-4 h-4 text-cyan-200 group-hover:translate-x-1 transition-transform" />
                </h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed font-sans">
                  <p>
                    At Fun Circle Games, we strive to ensure our creators are satisfied with our simulation professional tools. This Refund Policy describes the circumstances under which you may be eligible for a refund for purchases made on our Site.
                  </p>
                  <p className="p-4 bg-cyan-50/50 rounded-xl border border-cyan-100/50 text-sm">
                    This policy is designed to comply with consumer protection laws in the <strong className="text-cyan-900">United Kingdom (Consumer Rights Act 2015)</strong> and the <strong className="text-cyan-900">United States</strong>.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 2: Digital Products and Immediate Access */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all font-sans"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20">
                <Download className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6 w-full">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">2. Digital Products and Immediate Access</h2>
                <p className="text-zinc-600 font-medium leading-relaxed text-sm">
                  The Creator Studio is a digital product provided via <strong className="text-cyan-900">immediate download or streaming access</strong>. In accordance with digital consumer law:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 bg-cyan-50/30 rounded-3xl border border-cyan-100/50 space-y-4">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">UK/EU Statutory Cancellation</h3>
                    <p className="text-xs font-medium text-zinc-700 leading-relaxed">
                      By accessing the digital content, you expressly consent to begin the performance of the contract during the 14-day cancellation period and acknowledge that you thereby lose your right to cancel.
                    </p>
                  </div>
                  <div className="p-6 bg-cyan-100/20 rounded-3xl border border-cyan-200/50 space-y-4">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-700">Standard Sales</h3>
                    <p className="text-xs font-medium text-zinc-700 leading-relaxed">
                      All sales of digital content, including early access codes and subscription fees, are generally <strong>final and non-refundable</strong> once the content has been accessed or downloaded.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 3: Subscriptions, Trials and Renewals */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-cyan-50/20 p-8 md:p-10 rounded-[2rem] border border-cyan-100 font-sans"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight italic line-through decoration-cyan-400/30">3. Subscriptions & Trials</h2>
                <div className="space-y-4">
                  <div className="p-6 bg-white rounded-3xl border border-cyan-100 space-y-4">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600 italic">Automatic Renewals</h3>
                    <p className="text-zinc-600 font-medium leading-relaxed text-sm">
                      Subscriptions to Creator Pro and Studio Elite tiers automatically renew at the end of the billing cycle (Monthly or Yearly). You can cancel at any time via your dashboard. Cancellation prevents future billing but does not generate a pro-rated refund for the current cycle.
                    </p>
                  </div>
                  
                  <div className="p-6 bg-red-50 rounded-3xl border border-red-100 space-y-4 text-red-950">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-red-600 italic">Paid Trial Policy</h3>
                    <p className="text-sm font-medium leading-relaxed">
                      If you have signed up for a paid trial, <strong className="text-red-900 underline decoration-red-500">you will be automatically charged the full subscription fee</strong> upon the conclusion of the trial period unless cancelled at least 24 hours prior to the trial expiration.
                    </p>
                    <p className="text-xs opacity-70 italic font-bold">
                      Chargebacks initiated without prior contact for trial-to-paid conversions will be contested with full disclosure of these agreed terms.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 4: Refund Exceptions */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all font-sans"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <AlertCircle className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6 w-full">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">4. Refund Exceptions</h2>
                <p className="text-zinc-600 font-medium text-sm">
                  We may, at our sole discretion, issue a refund in the following limited cases:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { title: 'Technical Defects', desc: 'If software is proven defective and unresolved within 30 days of reporting.', icon: AlertCircle },
                    { title: 'Duplicate Charges', desc: 'Verified accidental duplicate transactions for the same identical service.', icon: CreditCard },
                    { title: 'Unauthorized', desc: 'Cases where a transaction was made without authorization (verification required).', icon: ShieldCheck }
                  ].map((item, i) => (
                    <div key={i} className="p-6 bg-cyan-50/50 rounded-3xl border border-cyan-100 space-y-3 relative group overflow-hidden">
                      <div className="absolute top-0 right-0 p-2 opacity-5 group-hover:opacity-10 transition-opacity">
                        <item.icon className="w-12 h-12 text-cyan-950" />
                      </div>
                      <h3 className="font-black uppercase tracking-widest text-[10px] text-cyan-400">{item.title}</h3>
                      <p className="text-xs font-medium text-cyan-950 leading-snug">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 5: How to Request */}
          <motion.section 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pt-8"
          >
            <div className="bg-white p-10 md:p-14 rounded-[3rem] border border-cyan-100 shadow-2xl shadow-cyan-950/5 relative overflow-hidden font-sans">
              {/* Background Accent */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-[100px] -z-0" />
              
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <h2 className="text-2xl md:text-4xl font-heavy font-display text-cyan-950 uppercase tracking-tight leading-none">
                    5. How to <br />
                    <span className="text-cyan-600/60">Request</span>
                  </h2>
                  <p className="text-zinc-500 font-medium text-sm">
                    To request an exception, please contact our billing department directly via email.
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-cyan-400">Required Information</h4>
                    <ul className="space-y-2">
                      {['Transaction ID', 'Account Email', 'Detailed Description'].map((info, i) => (
                        <li key={i} className="flex items-center gap-3 text-cyan-900/70 font-medium text-xs">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                          {info}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <motion.a 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href="mailto:support@funcirclegames.com?subject=Refund Exception Request"
                    className="flex flex-col gap-1 p-8 bg-cyan-50 border border-cyan-100 rounded-[2rem] shadow-xl group"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-white border border-cyan-100 rounded-2xl flex items-center justify-center">
                        <Mail className="w-6 h-6 text-cyan-600" />
                      </div>
                      <ExternalLink className="w-5 h-5 text-zinc-300 group-hover:text-cyan-500 transition-colors" />
                    </div>
                    <span className="text-[10px] font-black uppercase text-cyan-400 tracking-[0.2em]">Contact Billing</span>
                    <span className="text-lg font-bold text-cyan-950">support@funcirclegames.com</span>
                    <p className="mt-4 text-[10px] text-zinc-400 font-medium uppercase tracking-widest">
                      Average response time: 5 business days
                    </p>
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;

